import React, { useEffect, useState } from 'react'

import { Accordion, Box, ButtonColorVariant } from '@gousto-internal/citrus-react'
import PropTypes from 'prop-types'

import { iconNames } from '../Icon'
import { ExpandedContent } from './ExpandedContent'
import { StyledButton } from './SettingSection.styled'
import { SettingSectionToggle } from './SettingSectionToggle'

import css from './SettingSection.css'

const ExpandedSection = ({
  children,
  collapse,
  ctaText,
  instruction,
  isCtaDisabled,
  isExpanded,
  isMobile,
  onChildrenRender,
  onSubmit,
  testingSelector,
}) => {
  useEffect(() => {
    if (isExpanded) {
      onChildrenRender(collapse, isExpanded)
    }
  })

  const handleCollapseAndSubmit = () => {
    collapse()
    onSubmit()
  }

  return (
    <div className={css.expandedContent}>
      <ExpandedContent
        isExpanded={isExpanded}
        isMobile={isMobile}
        title={instruction}
        collapseSection={collapse}
      >
        {children}
        <Box paddingV={2}>
          <StyledButton
            disabled={isCtaDisabled}
            onClick={handleCollapseAndSubmit}
            data-testid={`${testingSelector}-save-cta`}
            colorVariant={ButtonColorVariant.Primary}
            width="100%"
          >
            {ctaText}
          </StyledButton>
        </Box>
      </ExpandedContent>
    </div>
  )
}

ExpandedSection.propTypes = {
  children: PropTypes.node.isRequired,
  collapse: PropTypes.func.isRequired,
  ctaText: PropTypes.string.isRequired,
  instruction: PropTypes.string.isRequired,
  isCtaDisabled: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  onChildrenRender: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  testingSelector: PropTypes.string.isRequired,
}

export const SettingSection = ({
  children,
  ctaText,
  icon,
  isMobile,
  onEditClick,
  onSubmit,
  renderCurrentValue,
  title,
  instruction,
  testingSelector,
  isCtaDisabled,
  onChildrenRender,
}) => {
  // Accordion keeps its own expanded state and only reports header toggles via onChange,
  // so mirror it here to support collapsing programmatically (CTA, mobile modal, onChildrenRender)
  const [isExpanded, setIsExpanded] = useState(false)

  const handleChange = (expanded) => {
    setIsExpanded(expanded)

    if (expanded) {
      onEditClick()
    }
  }

  return (
    <div className={css.expandableSection}>
      <Accordion
        id={`setting-section-${testingSelector}`}
        onChange={handleChange}
        title={(isAccordionExpanded) => (
          <SettingSectionToggle
            isExpanded={isAccordionExpanded}
            icon={icon}
            title={title}
            isMobile={isMobile}
            renderCurrentValue={renderCurrentValue}
            testingSelector={testingSelector}
          />
        )}
      >
        {({ setIsExpanded: setAccordionExpanded }) => (
          <ExpandedSection
            collapse={() => {
              setAccordionExpanded(false)
              setIsExpanded(false)
            }}
            ctaText={ctaText}
            instruction={instruction}
            isCtaDisabled={isCtaDisabled}
            isExpanded={isExpanded}
            isMobile={isMobile}
            onChildrenRender={onChildrenRender}
            onSubmit={onSubmit}
            testingSelector={testingSelector}
          >
            {children}
          </ExpandedSection>
        )}
      </Accordion>
    </div>
  )
}

SettingSection.propTypes = {
  icon: PropTypes.oneOf(iconNames).isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  renderCurrentValue: PropTypes.node.isRequired,
  ctaText: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  instruction: PropTypes.string.isRequired,
  testingSelector: PropTypes.string,
  isCtaDisabled: PropTypes.bool,
  onChildrenRender: PropTypes.func,
}

SettingSection.defaultProps = {
  isMobile: false,
  testingSelector: '',
  isCtaDisabled: false,
  onChildrenRender: () => {},
}
